import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import DispositionCard, { DispositionOption } from '../components/DispositionCard';
import OwnerReassignControl, { useProjectOwnership } from '../components/OwnerReassignControl';
import { NEEDS_BUSINESS_DECISION_LABEL, nfrStatusBadgeText } from '../lib/nfrStatus';

// #2543 — NFR gap-surfacing, agent-proposed & chat-managed. Third
// generalization of the Model-A page shape (RisksPage.tsx, #2544): a
// dedicated NfrsPage rather than a chat-embedded affordance, matching the
// precedent set by the directly-analogous prior story (both #2544's and
// #2543's own issues describe their flow as "chat-managed", but #2544's own
// instantiation is this same dedicated-page shape, not literal chat
// transcript embedding — this build follows what was actually built, not a
// re-reading of the word "chat").

// ── Types ─────────────────────────────────────────────────────────────────────

interface NfrDispositionProposal {
  id: string;
  disposition: 'ACCEPT' | 'NEEDS_BUSINESS_DECISION';
  proposedThreshold: string | null;
  basis: string | null;
}

interface Nfr {
  id: string;
  externalId: string | null;
  category: string;
  description: string;
  threshold: string | null;
  businessDecisionNote: string | null;
  notApplicableReason: string | null;
  status: 'GAP' | 'RESOLVED' | 'NEEDS_BUSINESS_DECISION' | 'NOT_APPLICABLE';
  // #2846 — the accountable project member (#2741's ownership model).
  ownerId: string | null;
  createdAt: string;
  proposal: NfrDispositionProposal | null;
}

// ── Style maps ────────────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  GAP:            { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b' },
  RESOLVED:       { bg: 'rgba(16,185,129,0.12)',  color: '#10b981' },
  NEEDS_BUSINESS_DECISION: { bg: 'rgba(99,102,241,0.12)', color: 'var(--primary)' },
  NOT_APPLICABLE: { bg: 'rgba(120,113,108,0.12)', color: 'var(--text-secondary)' },
};

// #2835 — an unrecognised status used to crash the page: STATUS_STYLE[x] was
// undefined and Badge read `.bg` off it. A status the server knows before this
// bundle does (e.g. a tab loaded before a deploy) now gets a neutral badge.
const FALLBACK_STATUS_STYLE = { bg: 'rgba(120,113,108,0.12)', color: 'var(--text-secondary)' };

const CATEGORY_LABEL: Record<string, string> = {
  performance: 'Performance', scalability: 'Scalability', availability: 'Availability',
  security: 'Security', reliability: 'Reliability', retention: 'Retention', accessibility: 'Accessibility',
};

const CATEGORY_ORDER = ['performance', 'scalability', 'availability', 'security', 'reliability', 'retention', 'accessibility'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function Badge({ text, style }: { text: string; style: { bg: string; color: string } }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 9999,
      fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-body)',
      background: style.bg, color: style.color, letterSpacing: '0.02em',
    }}>
      {text}
    </span>
  );
}

function resolvedText(nfr: Nfr): string | null {
  if (nfr.status === 'RESOLVED') return nfr.threshold;
  if (nfr.status === 'NEEDS_BUSINESS_DECISION') return nfr.businessDecisionNote ? `${NEEDS_BUSINESS_DECISION_LABEL}: ${nfr.businessDecisionNote}` : null;
  if (nfr.status === 'NOT_APPLICABLE') return nfr.notApplicableReason ? `Not applicable: ${nfr.notApplicableReason}` : null;
  return null;
}

// Parameterizes the shared DispositionCard (Part 1 of the #2544 shared core,
// generalized a third time here) with NFR's own option-set. Accept is the
// first real zero-field consumer of the card's own auto-accept-on-click
// path — RisksPage never used it (every Risk option required at least one
// field) — because Accept here means "confirm the agent's own proposed
// threshold verbatim", something the server already knows via proposalId,
// needing no typed input at all.
const DISPOSITION_OPTIONS: DispositionOption[] = [
  { key: 'ACCEPT', label: 'Accept', fields: [] },
  { key: 'MODIFY', label: 'Modify', fields: [{ key: 'threshold', label: 'Measurable threshold' }] },
  { key: 'NEEDS_BUSINESS_DECISION', label: NEEDS_BUSINESS_DECISION_LABEL, fields: [{ key: 'businessDecisionNote', label: 'What needs deciding' }] },
  { key: 'NOT_APPLICABLE', label: 'Not applicable', fields: [{ key: 'notApplicableReason', label: 'Reason' }] },
];

// ── Main page ─────────────────────────────────────────────────────────────────

export default function NfrsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const ownership = useProjectOwnership(projectId);

  const [nfrs, setNfrs] = useState<Nfr[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [generatingProposals, setGeneratingProposals] = useState(false);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const [dispositioningId, setDispositioningId] = useState<string | null>(null);
  const [dispositionError, setDispositionError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    apiFetch<Nfr[]>(`/projects/${projectId}/nfrs`)
      .then(setNfrs)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // #2544 convention — same "on demand, not automatic" trigger as Risk's own
  // "Generate agent proposals": an LLM call per run, never auto-triggered on
  // page load.
  const handleGenerateProposals = async () => {
    if (!projectId) return;
    setGeneratingProposals(true);
    setProposalError(null);
    try {
      await apiFetch(`/projects/${projectId}/nfrs/generate-disposition-proposals`, { method: 'POST' });
      load();
    } catch (e) {
      setProposalError(String(e));
    } finally {
      setGeneratingProposals(false);
    }
  };

  async function handleAcceptDisposition(nfr: Nfr, optionKey: string, fieldValues: Record<string, string>) {
    if (!projectId) return;
    setDispositioningId(nfr.id);
    setDispositionError(null);
    try {
      await apiFetch(`/projects/${projectId}/nfrs/${nfr.id}/accept-disposition`, {
        method: 'POST',
        body: JSON.stringify({
          proposalId: nfr.proposal?.id,
          disposition: optionKey,
          threshold: fieldValues.threshold,
          businessDecisionNote: fieldValues.businessDecisionNote,
          notApplicableReason: fieldValues.notApplicableReason,
        }),
      });
      load();
    } catch (e) {
      setDispositionError(String(e));
    } finally {
      setDispositioningId(null);
    }
  }

  if (loading) return <div style={{ padding: 24, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>Loading…</div>;
  if (error) return <div style={{ padding: 24, fontFamily: 'var(--font-body)', color: 'var(--error, #ef4444)' }}>Error: {error}</div>;

  const undisposedCount = nfrs.filter((n) => n.status === 'GAP').length;
  const byCategory = new Map<string, Nfr[]>();
  for (const nfr of nfrs) {
    const list = byCategory.get(nfr.category) ?? [];
    list.push(nfr);
    byCategory.set(nfr.category, list);
  }
  const orderedCategories = [
    ...CATEGORY_ORDER.filter((c) => byCategory.has(c)),
    ...[...byCategory.keys()].filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, margin: 0 }}>Non-Functional Requirements</h1>
        <button
          onClick={handleGenerateProposals}
          disabled={generatingProposals || undisposedCount === 0}
          style={{
            fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
            padding: '6px 14px', borderRadius: 'var(--radius-sm)',
            background: 'var(--primary)', color: '#fff', border: 'none',
            cursor: (generatingProposals || undisposedCount === 0) ? 'default' : 'pointer',
            opacity: (generatingProposals || undisposedCount === 0) ? 0.6 : 1,
          }}
        >
          {generatingProposals ? 'Generating…' : 'Generate agent proposals'}
        </button>
      </div>

      {proposalError && <p style={{ color: 'var(--error, #ef4444)', fontFamily: 'var(--font-body)', fontSize: 13 }}>{proposalError}</p>}
      {dispositionError && <p style={{ color: 'var(--error, #ef4444)', fontFamily: 'var(--font-body)', fontSize: 13 }}>{dispositionError}</p>}

      {nfrs.length === 0 && (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
          No non-functional requirements have been surfaced from the PRD yet.
        </p>
      )}

      {orderedCategories.map((category) => (
        <div key={category} style={{ marginBottom: 24 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 15, margin: '0 0 10px' }}>
            {CATEGORY_LABEL[category] ?? category}
          </h2>
          {(byCategory.get(category) ?? []).map((nfr) => {
            const resolved = resolvedText(nfr);
            return (
              <div key={nfr.id} style={{
                padding: '12px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                background: 'var(--surface)', marginBottom: 10,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div>
                    {nfr.externalId && (
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginRight: 8 }}>
                        {nfr.externalId}
                      </span>
                    )}
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>{nfr.description}</span>
                  </div>
                  <Badge text={nfrStatusBadgeText(nfr.status)} style={STATUS_STYLE[nfr.status] ?? FALLBACK_STATUS_STYLE} />
                </div>

                {resolved && (
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0 0' }}>
                    {resolved}
                  </p>
                )}

                <OwnerReassignControl
                  projectId={projectId!}
                  entityType="nfr"
                  entityId={nfr.id}
                  ownerId={nfr.ownerId}
                  ownership={ownership}
                  onReassigned={(newOwnerId) =>
                    setNfrs(prev => prev.map(x => (x.id === nfr.id ? { ...x, ownerId: newOwnerId } : x)))}
                />

                {nfr.status === 'GAP' && (
                  <DispositionCard
                    proposal={nfr.proposal ? {
                      optionLabel: nfr.proposal.disposition === 'ACCEPT' ? 'Accept' : NEEDS_BUSINESS_DECISION_LABEL,
                      summary: nfr.proposal.proposedThreshold ?? 'This requirement needs business/ops input rather than an agent-proposed threshold.',
                      basis: nfr.proposal.basis,
                    } : null}
                    options={DISPOSITION_OPTIONS}
                    defaultOptionKey={nfr.proposal?.disposition}
                    defaultFieldValues={nfr.proposal?.proposedThreshold ? { threshold: nfr.proposal.proposedThreshold } : undefined}
                    submitting={dispositioningId === nfr.id}
                    errorMessage={dispositioningId === nfr.id ? dispositionError : null}
                    onAccept={(optionKey, fieldValues) => void handleAcceptDisposition(nfr, optionKey, fieldValues)}
                  />
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
